import { Link, useSearchParams } from "react-router-dom";
import { Reveal } from "@/components/site/Reveal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const LAST_UPDATED = "December 2025";

export default function Legal() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab") || "privacy";

  const onChange = (value) => {
    setParams({ tab: value });
  };

  return (
    <div data-testid="legal-page" className="pt-32 pb-20">
      <section className="mx-auto max-w-7xl px-6 md:px-12 mesh-bg pb-12">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">
            <Link to="/" className="hover:text-foreground">Home</Link> · Legal
          </p>
          <h1 className="mt-5 font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Legal & <span className="text-gradient">policies</span>.
          </h1>
          <p className="mt-7 text-lg text-zinc-400 max-w-2xl">
            The fine print. Last updated {LAST_UPDATED}.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-4xl px-6 md:px-12 mt-10">
        <Tabs value={initial} onValueChange={onChange} className="w-full">
          <TabsList className="grid grid-cols-3 max-w-md rounded-full bg-white/[0.03] border border-white/10 p-1">
            <TabsTrigger value="privacy" data-testid="legal-tab-privacy" className="rounded-full data-[state=active]:bg-nexus-cyan data-[state=active]:text-nexus-ink">
              Privacy
            </TabsTrigger>
            <TabsTrigger value="terms" data-testid="legal-tab-terms" className="rounded-full data-[state=active]:bg-nexus-cyan data-[state=active]:text-nexus-ink">
              Terms
            </TabsTrigger>
            <TabsTrigger value="cookies" data-testid="legal-tab-cookies" className="rounded-full data-[state=active]:bg-nexus-cyan data-[state=active]:text-nexus-ink">
              Cookies
            </TabsTrigger>
          </TabsList>

          <TabsContent value="privacy" className="mt-10">
            <article data-testid="legal-privacy" className="rounded-3xl glass border-white/[0.08] p-8 md:p-12 space-y-6 prose-content">
              <Section title="Overview">
                Fullstack Endeavours ("we", "us") respects your privacy. This policy explains what we collect, why, and how to control it.
              </Section>
              <Section title="What we collect">
                We collect information you provide directly — name, email, company, project details — through forms and email. We also collect basic analytics (page views, referrers) via privacy-friendly tooling.
              </Section>
              <Section title="How we use it">
                To reply to your inquiry, deliver services you've contracted, and improve our website. We do not sell your data.
              </Section>
              <Section title="Sharing">
                We share data only with vetted sub-processors required to deliver services (e.g. hosting, email). All sub-processors are bound by data-processing agreements.
              </Section>
              <Section title="Your rights">
                You can request access, correction, or deletion of your data at any time by emailing admin@fullstackendeavours.in. We respond within 30 days.
              </Section>
              <Section title="Retention">
                We keep contact submissions for 24 months. Newsletter subscriptions are kept until you unsubscribe. Project data is retained per the engagement contract.
              </Section>
            </article>
          </TabsContent>

          <TabsContent value="terms" className="mt-10">
            <article data-testid="legal-terms" className="rounded-3xl glass border-white/[0.08] p-8 md:p-12 space-y-6 prose-content">
              <Section title="Acceptance">
                By accessing the Fullstack Endeavours website you agree to these terms. If you do not agree, please do not use the site.
              </Section>
              <Section title="Services">
                All engagements are governed by an executed Statement of Work (SOW). Public pricing on this site is indicative — quoted estimates after a discovery phase are binding.
              </Section>
              <Section title="Intellectual property">
                Site content (text, graphics, code samples) is © Fullstack Endeavours unless noted. Code delivered under a client SOW transfers per the IP transfer clause of that SOW.
              </Section>
              <Section title="Warranties & liability">
                The site is provided "as is". We make no warranties as to availability or fitness for a particular purpose. Liability is limited to fees paid in the prior 12 months under the relevant engagement.
              </Section>
              <Section title="Governing law">
                These terms are governed by the laws of the State of New York, USA. Disputes will be resolved in New York County courts.
              </Section>
              <Section title="Changes">
                We may update these terms occasionally. Material changes will be noted at the top of this page with the updated date.
              </Section>
            </article>
          </TabsContent>

          <TabsContent value="cookies" className="mt-10">
            <article data-testid="legal-cookies" className="rounded-3xl glass border-white/[0.08] p-8 md:p-12 space-y-6 prose-content">
              <Section title="What we use">
                We use a small number of essential cookies (session, CSRF) and one analytics cookie (page views, no individual tracking).
              </Section>
              <Section title="Categories">
                Essential — required for the site to function. Cannot be disabled. Analytics — optional, anonymized usage statistics. Marketing — we don't use marketing cookies.
              </Section>
              <Section title="Managing cookies">
                Most browsers let you disable cookies in settings. Disabling essential cookies may break parts of the site.
              </Section>
              <Section title="Contact">
                Questions? Email admin@fullstackendeavours.in.
              </Section>
            </article>
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-2xl md:text-3xl tracking-tight">{title}</h2>
      <p className="mt-3 text-zinc-300 leading-[1.8] text-base md:text-lg">{children}</p>
    </div>
  );
}
